import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useBrand } from '../lib/brand';
import StagingEnvBanner from '../components/StagingEnvBanner';
import {
  FeaturesGlyph,
  HeaderIconTile,
  HEADER_GREEN,
  HEADER_NAVY,
  HEADER_ORANGE,
  HEADER_TEAL,
  MenuButton,
  MicGlyph,
  PricingGlyph,
  StoryGlyph,
} from '../components/publicHeaderIcons';
import { navLinks } from './content';
import { Wordmark } from './wave';

const navIcons = {
  '/features': { icon: <FeaturesGlyph />, color: HEADER_NAVY },
  '/coaches': { icon: <MicGlyph />, color: HEADER_TEAL },
  '/pricing': { icon: <PricingGlyph />, color: HEADER_GREEN },
  '/story': { icon: <StoryGlyph />, color: HEADER_ORANGE },
} as const;

export { Wordmark };

export function usePageMeta(title: string, description: string) {
  const brand = useBrand();
  useEffect(() => {
    document.title = title;
    let tag = document.querySelector('meta[name="description"]');
    if (!tag) {
      tag = document.createElement('meta');
      tag.setAttribute('name', 'description');
      document.head.appendChild(tag);
    }
    const previous = tag.getAttribute('content');
    tag.setAttribute('content', description);
    const theme = document.querySelector('meta[name="theme-color"]');
    const previousTheme = theme?.getAttribute('content') ?? null;
    theme?.setAttribute('content', '#ffffff');
    setMeta('og:title', title, 'property');
    setMeta('og:description', description, 'property');
    setMeta('og:image', '/og.png', 'property');
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', title);
    setMeta('twitter:description', description);
    setMeta('twitter:image', '/og.png');
    return () => {
      if (previous == null) tag?.removeAttribute('content');
      else tag?.setAttribute('content', previous);
      if (previousTheme) theme?.setAttribute('content', previousTheme);
    };
  }, [title, description, brand.name]);
}

function setMeta(name: string, content: string, attr: 'name' | 'property' = 'name') {
  let tag = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attr, name);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
}

const demoClass =
  'inline-flex h-11 shrink-0 items-center justify-center rounded-full bg-[#00aaa4] px-3.5 text-[14px] font-bold tracking-tight text-[#04221f] shadow-sm hover:bg-[#2ec4be] sm:px-5 sm:text-[15px]';

export function MarketingShell({ children }: { children: React.ReactNode }) {
  const brand = useBrand();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [headerH, setHeaderH] = useState(76);
  const headerRef = useRef<HTMLElement>(null);
  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `rounded-full px-3.5 py-2 text-[15px] font-semibold tracking-tight ${
      isActive ? 'bg-[#e7f6f5] text-[#00736f]' : 'text-[#1d1d1f] hover:bg-[#f5f5f7]'
    }`;

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    const node = headerRef.current;
    if (!node) return;
    const measure = () => setHeaderH(node.getBoundingClientRect().height);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="mkt min-h-screen bg-white text-mkt-ink">
      <a
        href="#content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-3 focus:z-[60] focus:rounded-full focus:bg-white focus:px-4 focus:py-2"
      >
        Skip to content
      </a>
      <header ref={headerRef} className="sticky top-0 z-50" data-mkt-header>
        <StagingEnvBanner />
        <div className="border-b border-black/5 bg-white/75 text-[#1d1d1f] shadow-[0_8px_30px_rgba(13,63,103,0.06)] backdrop-blur-xl backdrop-saturate-150">
          <nav className="relative mx-auto flex h-[76px] max-w-[1200px] items-center gap-3 px-3 sm:gap-4 sm:px-6" aria-label="Primary">
            <Link to="/" className="relative z-10 shrink-0" aria-label={`${brand.name} CRM home`}>
              <Wordmark tone="ink" />
            </Link>
            <div className="absolute left-1/2 hidden -translate-x-1/2 items-center gap-1 xl:flex">
              {navLinks.map((item) => (
                <NavLink key={item.to} to={item.to} end={item.end} className={linkClass}>
                  {item.label}
                </NavLink>
              ))}
            </div>
            <div className="relative z-10 ml-auto flex shrink-0 items-center gap-2 sm:gap-3">
              <Link to="/login" className="hidden text-[15px] font-semibold text-[#1d1d1f] hover:text-[#00736f] xl:inline">
                Sign in
              </Link>
              <Link to="/contact" className={demoClass} onClick={() => setOpen(false)}>
                Book a demo
              </Link>
              <MenuButton open={open} onClick={() => setOpen((value) => !value)} controls="mkt-menu" color={HEADER_NAVY} />
            </div>
          </nav>
        </div>
      </header>
      {open ? (
        <div
          id="mkt-menu"
          className="mkt-sheet fixed inset-x-0 bottom-0 z-40 overflow-y-auto bg-[#f5f5f7]/95 px-4 pb-10 pt-5 text-[#1d1d1f] backdrop-blur-2xl xl:hidden"
          style={{ top: headerH }}
        >
          <nav aria-label="Mobile" className="mx-auto flex max-w-lg flex-col gap-3">
            {navLinks.map((item) => {
              const mark = navIcons[item.to];
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className="flex items-center gap-4 rounded-3xl bg-white px-4 py-3 shadow-sm ring-1 ring-black/5"
                  onClick={() => setOpen(false)}
                >
                  <HeaderIconTile color={mark.color}>{mark.icon}</HeaderIconTile>
                  <span className="text-xl font-bold tracking-tight">{item.label}</span>
                </NavLink>
              );
            })}
            <Link
              to="/login"
              className="flex h-14 items-center justify-center rounded-full bg-white text-[17px] font-semibold text-[#1d1d1f] shadow-sm ring-1 ring-black/10"
              onClick={() => setOpen(false)}
            >
              Sign in
            </Link>
            <Link to="/contact" className={demoClass} onClick={() => setOpen(false)}>
              Book a demo
            </Link>
          </nav>
        </div>
      ) : null}
      <main id="content">{children}</main>
      <SiteFooter />
    </div>
  );
}

export function Pill({
  to,
  href,
  children,
  variant = 'primary',
  surface = 'light',
  onClick,
}: {
  to?: string;
  href?: string;
  children: React.ReactNode;
  variant?: 'primary' | 'secondary';
  surface?: 'light' | 'dark';
  onClick?: () => void;
}) {
  const className =
    surface === 'dark'
      ? variant === 'primary'
        ? 'inline-flex h-11 items-center justify-center rounded-full bg-white px-6 text-mkt-body text-mkt-accent hover:bg-mkt-canvas'
        : 'inline-flex h-11 items-center justify-center rounded-full bg-transparent px-6 text-mkt-body text-white ring-1 ring-white hover:bg-white/10'
      : variant === 'primary'
        ? 'inline-flex h-11 items-center justify-center rounded-full bg-mkt-accent px-6 text-mkt-body text-white hover:bg-mkt-accent-hover'
        : 'inline-flex h-11 items-center justify-center rounded-full bg-white px-6 text-mkt-body text-mkt-accent ring-1 ring-mkt-accent hover:bg-mkt-canvas';
  if (to) {
    return (
      <Link to={to} className={className} onClick={onClick}>
        {children}
      </Link>
    );
  }
  return (
    <a href={href} className={className} onClick={onClick}>
      {children}
    </a>
  );
}

function SiteFooter() {
  const brand = useBrand();
  return (
    <footer className="mkt-band-deep text-white" data-mkt-surface="dark">
      <div className="mx-auto max-w-copy px-6 pt-12">
        <Link to="/" className="inline-flex" aria-label={`${brand.name} CRM for swim clubs home`}>
          <Wordmark tone="light" variant="full" />
        </Link>
      </div>
      <div className="mx-auto grid max-w-copy gap-8 px-6 py-12 sm:grid-cols-3">
        <div>
          <p className="text-[12px] font-semibold text-white">Product</p>
          <ul className="mt-3 space-y-2 text-[12px]">
            <li><Link to="/coaches" className="text-white underline-offset-4 hover:underline">Coaches</Link></li>
            <li><Link to="/features" className="text-white underline-offset-4 hover:underline">Features</Link></li>
            <li><Link to="/pricing" className="text-white underline-offset-4 hover:underline">Pricing</Link></li>
          </ul>
        </div>
        <div>
          <p className="text-[12px] font-semibold text-white">Company</p>
          <ul className="mt-3 space-y-2 text-[12px]">
            <li><Link to="/story" className="text-white underline-offset-4 hover:underline">Story</Link></li>
            <li><Link to="/contact" className="text-white underline-offset-4 hover:underline">Contact</Link></li>
            <li><Link to="/login" className="text-white underline-offset-4 hover:underline">Sign in</Link></li>
          </ul>
        </div>
        <div>
          <p className="text-[12px] font-semibold text-white">Legal</p>
          <ul className="mt-3 space-y-2 text-[12px]">
            <li><Link to="/privacy" className="text-white underline-offset-4 hover:underline">Privacy</Link></li>
            <li><Link to="/terms" className="text-white underline-offset-4 hover:underline">Terms</Link></li>
          </ul>
        </div>
      </div>
      <div className="mx-auto flex max-w-copy flex-col gap-2 border-t border-white/20 px-6 py-6 text-[12px] text-white sm:flex-row sm:justify-between">
        <p>Copyright © {new Date().getFullYear()} {brand.name}</p>
        <p>Built in Aotearoa</p>
      </div>
    </footer>
  );
}

export function SectionHeading({
  title,
  sub,
  align = 'center',
  level = 'h2',
  tone = 'ink',
}: {
  title: string;
  sub?: string;
  align?: 'center' | 'left';
  level?: 'h1' | 'h2';
  tone?: 'ink' | 'light';
}) {
  const alignClass = align === 'center' ? 'mx-auto text-center' : 'text-left';
  const Tag = level;
  const titleColor = tone === 'light' ? 'text-white' : 'text-mkt-ink';
  const subColor = tone === 'light' ? 'text-white' : 'text-mkt-secondary';
  return (
    <div className={`max-w-copy ${alignClass}`}>
      <Tag className={`text-balance text-mkt-section-sm md:text-mkt-section ${titleColor}`}>{title}</Tag>
      {sub ? (
        <p className={`mt-5 text-balance text-mkt-sub-sm md:text-mkt-sub ${subColor}`}>{sub}</p>
      ) : null}
    </div>
  );
}
